import { HttpClient } from "@angular/common/http";
import { Component, Input } from "@angular/core";
import { GetJourneyById } from "src/app/core/models/journeys/get-journey-by-id.model";
import { AppToastService } from "src/app/core/services/app-toast.service";
import { JourneyService } from "src/app/core/services/journey.service";
import { UtilsService } from "src/app/core/services/utils.service";

const STATIC_IMPORT_TEMPLATE_ASSET = "assets/files/MRH360 - IMPORT COLAB x FOLGA.xlsx";
const STATIC_IMPORT_TEMPLATE_FILENAME = "MRH360 - IMPORT COLAB x FOLGA.xlsx";

@Component({
  selector: "app-download-and-upload-dayoff",
  templateUrl: "./download-and-upload-dayoff.component.html",
  styleUrls: ["./download-and-upload-dayoff.component.scss"],
})
export class DownloadAndUploadDayoffComponent {
  @Input() journey?: GetJourneyById;
  @Input() isManager = false;
  @Input() uploadLinkText?: string | null;
  @Input() figmaDropzoneLayout = false;
  /**
   * Quando true, o arquivo só é enviado ao clicar em Salvar (POST import).
   * Quando false, mantém o comportamento anterior: import imediato ao selecionar.
   */
  @Input() useSaveCancelActions = false;
  @Input() useStaticImportTemplate = false;

  get hasCustomUploadLink(): boolean {
    return (
      typeof this.uploadLinkText === "string" &&
      this.uploadLinkText.trim().length > 0
    );
  }

  get effectiveUploadPrompt(): string {
    return this.hasCustomUploadLink ? "" : "clique";
  }

  get effectiveUploadButtonText(): string {
    if (this.hasCustomUploadLink && typeof this.uploadLinkText === "string") {
      return this.uploadLinkText.trim();
    }
    return "aqui para selecionar.";
  }

  constructor(
    private journeyService: JourneyService,
    private utilsService: UtilsService,
    private toast: AppToastService,
    private http: HttpClient,
  ) {}

  docImage: any;

  attachedFile: string;
  attachedFileBase64: string;
  attachedFileExtension: string;

  documentFiles: any[] = [];
  extensions: string[] = [];

  /** Arquivo escolhido aguardando Salvar (modo useSaveCancelActions). */
  pendingImport: { base64: string; fileName: string; extension: string } | null =
    null;

  savingImport = false;

  downloadLastDocument() {
    if (this.useStaticImportTemplate) {
      this.http.get(STATIC_IMPORT_TEMPLATE_ASSET, { responseType: "blob" }).subscribe({
        next: (blob) =>
          this.utilsService.saveBlob(blob, STATIC_IMPORT_TEMPLATE_FILENAME),
        error: () =>
          this.toast.show("Erro!", "Não foi possível baixar o modelo da planilha."),
      });
      return;
    }
    if (!this.journey?.id) {
      this.toast.show("Aviso", "Não foi possível baixar a tabela desta jornada.");
      return;
    }
    this.journeyService.getLastDocument(this.journey.id).subscribe((res) => {
      this.utilsService.saveBlob(res, "planilha-folgas.xlsx");
    });
  }

  get canSaveImport(): boolean {
    return !!this.pendingImport && !this.savingImport;
  }

  get canCancelImport(): boolean {
    return !!this.pendingImport && !this.savingImport;
  }

  onCancelImport(): void {
    if (!this.canCancelImport) return;
    this.pendingImport = null;
    this.docImage = undefined;
  }

  onSaveImport(): void {
    const { base64, fileName, extension } = this.pendingImport;
    this.savingImport = true;
    this.journeyService
      .importTable(this.journey ? this.journey.id.toString() : '', base64, fileName, extension)
      .subscribe({
        next: (res) => {
          this.savingImport = false;
          const { totalError, totalImport } = res.data;
          let message = "";

          if (totalImport && totalImport > 0)
            message += `${totalImport} folga${totalImport === 1 ? "" : "s"} importada${totalImport > 1 ? "s" : ""} com sucesso. `;
          if (totalError && totalError > 0)
            message += `${totalError} folga${totalError === 1 ? "" : "s"} não fo${totalError === 1 ? "i" : "ram"} importada${totalError > 1 ? "s" : ""}. `;

          this.toast.show("Sucesso!", message);
          this.pendingImport = null;
        },
        error: (responseError: any) => {
          this.savingImport = false;
          const desc =
            responseError?.error?.errors?.[0]?.description ?? "Falha ao importar.";
          this.toast.show("Erro!", desc);
        },
      });
  }

  selectedDocument = ($event: any) => {
    if (!$event || Object.keys($event).length === 0) {
      this.pendingImport = null;
      this.docImage = undefined;
      return;
    }

    const { base64, fileName, extension } = $event;
    this.docImage = this.formatImage(extension, base64);

    if (this.useSaveCancelActions) {
      this.pendingImport = { base64, fileName, extension };
      return;
    }

    this.journeyService
      .importTable(this.journey ? this.journey.id.toString() : '', base64, fileName, extension)
      .subscribe({
        next: (res) => {
          const { totalError, totalImport } = res.data;
          let message = "";

          if (totalImport && totalImport > 0)
            message += `${totalImport} folga${totalImport === 1 ? "" : "s"} importada${totalImport > 1 ? "s" : ""} com sucesso. `;
          if (totalError && totalError > 0)
            message += `${totalError} folga${totalError === 1 ? "" : "s"} não fo${totalError === 1 ? "i" : "ram"} importada${totalError > 1 ? "s" : ""}. `;

          this.toast.show("Sucesso!", message);
        },
        error: (responseError: any) => {
          const desc =
            responseError?.error?.errors?.[0]?.description ?? "Falha ao importar.";
          this.toast.show("Erro!", desc);
        },
      });
  };

  private formatImage = (extension: any, base64: any): string => {
    if (extension?.toString().toLowerCase() === "pdf") {
      return "../../../../assets/images/pdf-logo.png";
    }
    return extension && `data:application/${extension};base64,${base64}`;
  };

  downloadFromUrl = (link) => this.utilsService.downloadImageFrom(link);
}
